import { useState, useEffect, useMemo } from "react";
import { useTips } from "@/hooks/useTips";

export interface SearchResult {
  id: string;
  title: string;
  content: string;
  category: string | null;
  image_url: string | null;
  youtube_url: string | null;
  rating: number | null;
  rating_count: number | null;
  created_at: string;
  published_at: string | null;
  is_published: boolean;
  relevanceScore: number;
}

export const useSearch = () => {
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const { tips, isLoading } = useTips();

  const searchResults = useMemo(() => {
    if (!query.trim() || !tips) {
      return [];
    }

    const searchTerm = query.toLowerCase().trim();
    const publishedTips = tips.filter(tip => tip.is_published);

    return publishedTips
      .map(tip => {
        let relevanceScore = 0;
        const titleLower = tip.title.toLowerCase();
        const contentLower = tip.content.toLowerCase();
        const categoryLower = tip.category?.toLowerCase() || "";

        // Title matches get highest score
        if (titleLower.includes(searchTerm)) {
          relevanceScore += 100;
          // Exact title match gets bonus
          if (titleLower === searchTerm) {
            relevanceScore += 50;
          }
          // Title starts with search term gets bonus
          if (titleLower.startsWith(searchTerm)) {
            relevanceScore += 30;
          }
        }

        // Content matches get medium score
        if (contentLower.includes(searchTerm)) {
          relevanceScore += 50;
        }

        // Category matches get lower score
        if (categoryLower.includes(searchTerm)) {
          relevanceScore += 25;
        }

        // Word boundary matches get bonus points
        const wordBoundaryRegex = new RegExp(`\\b${searchTerm}\\b`, 'i');
        if (wordBoundaryRegex.test(tip.title)) {
          relevanceScore += 20;
        }
        if (wordBoundaryRegex.test(tip.content)) {
          relevanceScore += 10;
        }

        // Only return tips with some relevance
        if (relevanceScore > 0) {
          return {
            ...tip,
            relevanceScore,
          } as SearchResult;
        }
        return null;
      })
      .filter((tip): tip is SearchResult => tip !== null)
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
  }, [query, tips]);

  const search = (searchQuery: string) => {
    setIsSearching(true);
    setQuery(searchQuery);
    
    // Simulate brief loading state for better UX
    setTimeout(() => {
      setIsSearching(false);
    }, 100);
  };

  const clearSearch = () => {
    setQuery("");
    setIsSearching(false);
  };

  return {
    query,
    searchResults,
    isSearching: isSearching || isLoading,
    search,
    clearSearch,
    hasResults: searchResults.length > 0,
    resultCount: searchResults.length,
  };
};